import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Collapse,
  Divider,
  Avatar,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as GenerateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import factureService from '../../API/factureService';
import AlertBar from '../../Components/AlertBar';
import { motion } from 'framer-motion';
import ButtonAttribute from '../../Components/ButtonAttribute';
import AutocompleteComboBox from '../../Components/AutocompleteComboBox';
import DateTimePickerComponent from '../../Components/DateTimePicker';
import DropDownList from '../../Components/DropDownList';

const FacturesList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(new Date().getMonth() + 1);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  // Filtres
  const [filterOpen, setFilterOpen] = useState(true);
  const [filters, setFilters] = useState({
    client_id: '',
    statut: 'brouillon',
    date_debut: null,
    date_fin: null
  });

  // Options pour les filtres
  const statutOptions = [
    { value: '', label: 'Tous', color: 'default' },
    { value: 'brouillon', label: 'Brouillon', color: 'default' },
    { value: 'envoyée', label: 'Envoyée', color: 'primary' },
    { value: 'payée', label: 'Payée', color: 'success' }
  ];

  // Liste des mois pour le dialogue de génération
  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  useEffect(() => {

    fetchFactures();
    fetchClients();
  }, [page, rowsPerPage, filters]);

  const fetchFactures = async () => {
    try {
      setLoading(true);
      const response = await factureService.getFactures(page + 1, rowsPerPage, filters);

      if (response && response.data && Array.isArray(response.data)) {
        setFactures(response.data);
        setTotalRows(response.total || 0);
      } else {
        console.error("Invalid response format:", response);
        setFactures([]);
        setTotalRows(0);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching factures:", error);
      setAlert({
        open: true,
        message: 'Erreur lors du chargement des factures: ' + (error.message || 'Erreur inconnue'),
        severity: 'error'
      });
      setFactures([]);
      setTotalRows(0);
      setLoading(false);
    }
  };
  const FullPageLoader = ({ loading, text = "Chargement..." }) => {
    if (!loading) return null;

    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255,255,255,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(3px)"
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2, fontWeight: "bold" }}>
            {text}
          </Typography>
        </Box>
      </Box>
    );
  };
  const fetchClients = async () => {
    try {
      const response = await factureService.getCreateData();
      if (response && response.clients) {
        setClients(response.clients);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Fonctions de gestion des événements
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateFilterChange = (name, date) => {
    setFilters(prev => ({ ...prev, [name]: date }));
  };

  const resetFilters = () => {
    setFilters({
      client_id: '',
      statut: 'brouillon',
      date_debut: null,
      date_fin: null
    });
    setAlert({
      open: true,
      message: 'Filtres réinitialisés',
      severity: 'info'
    });
  };

  // Fonctions pour les actions sur les factures
  const handleCreateFacture = () => {
    navigate('/factures/create');
  };

  const handleEditFacture = (id) => {
    navigate(`/factures/edit/${id}`);
  };

  const handleViewFacture = (id) => {
    navigate(`/factures/view/${id}`);
  };

  const handleDeleteClick = (facture) => {
    setFactureToDelete(facture);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await factureService.deleteFacture(factureToDelete.ID_Facture);
      setAlert({
        open: true,
        message: 'Facture supprimée avec succès',
        severity: 'success'
      });
      fetchFactures();
    } catch (error) {
      console.error("Error deleting facture:", error);
      setAlert({
        open: true,
        message: 'Erreur lors de la suppression de la facture',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setFactureToDelete(null);
    }
  };

  const handleGenerateClick = () => {
    fetchClients();
    setSelectedClient('');
    setGenerateDialogOpen(true);
  };

  const handleGenerateConfirm = async () => {
    try {
      const clientIds = selectedClient ? [selectedClient] : null;
      const response = await factureService.generateMonthlyFactures(generateMonth, generateYear, clientIds);
      setAlert({
        open: true,
        message: `${response.message || 'Factures générées avec succès'}`,
        severity: 'success'
      });
      fetchFactures();
    } catch (error) {
      console.error("Error generating monthly factures:", error);
      setAlert({
        open: true,
        message: 'Erreur lors de la génération des factures mensuelles',
        severity: 'error'
      });
    } finally {
      setGenerateDialogOpen(false);
    }
  };

  const handleDownloadPDF = async (facture) => {
    try {
      setLoadingPDF(true);

      await factureService.generatePDF(facture);

      setAlert({
        open: true,
        message: "PDF généré et téléchargé avec succès",
        severity: "success"
      });

    } catch (error) {

      console.error("Error downloading PDF:", error);

      setAlert({
        open: true,
        message: "Erreur lors du téléchargement du PDF",
        severity: "error"
      });

    } finally {
      setLoadingPDF(false);
    }
  };

  const handleDownloadExcel = async (facture) => {
    try {
      setLoadingExcel(true);

      await factureService.generateExcelOperations(facture);

      setAlert({
        open: true,
        message: "Excel généré et téléchargé avec succès",
        severity: "success"
      });

    } catch (error) {

      console.error("Error exporting to Excel:", error);

      setAlert({
        open: true,
        message: "Erreur lors de l'exportation vers Excel",
        severity: "error"
      });

    } finally {
      setLoadingExcel(false);
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (statut) => {
    switch (statut) {
      case 'brouillon':
        return 'default';
      case 'envoyée':
        return 'primary';
      case 'payée':
        return 'success';
      default:
        return 'default';
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Date invalide';
    }
  };

  return (
    <>
    <FullPageLoader
        loading={loadingPDF}
        text="Génération du PDF..."
      />
      <FullPageLoader
        loading={loadingExcel}
        text="Génération du Excel..."
      />
      <FullPageLoader
        loading={loadingEmail}
        text="Envoi de la facture par email..."
      />
      <Box sx={{
        backgroundColor: '#f8fafc',
        padding: { xs: '16px', md: '20px' },
        minHeight: '116vh',
        transition: 'all 0.3s ease-in-out'
      }}>
        <AlertBar
          open={alert.open}
          textContent={alert.message}
          alertColor={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* En-tête */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb4b',
            borderRadius: '32px',
            padding: { xs: '20px', md: '30px' },
            marginBottom: '20px',
            boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
          }}>
            <Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
                paddingBottom: '16px',
                borderBottom: '1px solid #f3f4f6',
                gap: { xs: '8px', md: '12px' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Box sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ReceiptIcon style={{ color: 'white', fontSize: '20px' }} />
                  </Box>
                  <Typography variant="h4" sx={{
                    fontWeight: 600,
                    color: '#1f2937',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    letterSpacing: '-0.025em'
                  }}>
                    Gestion des Factures
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: '#f9fafb',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',

                }}>
                  {factures.length} facture{factures.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              <Typography variant="body1" sx={{
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Créez, modifiez et suivez toutes vos factures
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <ButtonAttribute
                type="button"
                Bgcolor="transparent"
                color="#1976d2"
                bgColorHover="rgba(25,118,210,0.08)"
                ColorHover="#1976d2"
                startIcon={<GenerateIcon />}
                Label="Génération Auto"
                width="auto"
                height="45px"
                sx={{
                  border: '1.5px solid #1976d2',

                  fontWeight: 500,
                }}
                action={handleGenerateClick}

              />

              <ButtonAttribute
                type="button"
                Bgcolor="#1976d2"
                color="#fff"
                bgColorHover="#115293"
                ColorHover="#fff"
                startIcon={<AddIcon />}
                Label="Nouvelle Facture"
                width="auto"
                height="45px"
                action={handleCreateFacture}

              />

            </Box>
          </Box>

          {/* Filtres */}
          <Card
            elevation={2}
            sx={{
              mb: 3,

              overflow: 'visible',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: filterOpen ? 2 : 0
                }}
              >
                {/* <ButtonAttribute
                  type="button"
                  Bgcolor="transparent"
                  color="#1976d2"
                  bgColorHover="rgba(25,118,210,0.08)"
                  ColorHover="#1976d2"
                  startIcon={<FilterIcon />}
                  Label={filterOpen ? 'Masquer les filtres' : 'Afficher les filtres'}
                  width="auto"
                  height="36px"
                  action={() => setFilterOpen(!filterOpen)}
                  sx={{
                    fontWeight: 500,
                    borderRadius: "8px",
                    padding: "6px 16px",
                  }}
                /> */}

                {filterOpen && (
                  <ButtonAttribute
                    type="button"
                    Bgcolor="#f5f5f5"
                    color="#333"
                    bgColorHover="#e0e0e0"
                    ColorHover="#000"
                    startIcon={<RefreshIcon />}
                    Label="Réinitialiser"
                    width="auto"
                    height="32px"
                    action={resetFilters}
                    sx={{
                      fontWeight: 500,
                      borderRadius: "8px",
                      padding: "4px 12px",
                    }}
                  />
                )}
              </Box>


              <Collapse in={filterOpen}>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>

                  {/* 🔵 Client */}
                  <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteComboBox
                      list={clients}
                      Label="Client"
                      keyField="ID_Client"
                      valueField="Raison_sociale"
                      required={false}
                      value={filters.client_id}
                      onChange={(selected) => {
                        handleFilterChange({
                          target: { name: "client_id", value: selected },
                        });
                      }}
                      widthSpecific="100%"
                    />
                  </Grid>

                  {/* 🟣 Statut */}
                  <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteComboBox
                      list={statutOptions}
                      Label="Statut"
                      keyField="value"
                      valueField="label"
                      required={false}
                      value={filters.statut}
                      onChange={(selected) =>
                        handleFilterChange({
                          target: { name: "statut", value: selected },
                        })
                      }
                      widthSpecific="100%"
                    />
                  </Grid>


                  {/* 🟢 Date Début */}
                  <Grid item xs={12} sm={6} md={3}>
                    <DateTimePickerComponent
                      label="Date début"
                      typeDate="date"
                      value={filters.date_debut}
                      dateSelected={(val) => handleDateFilterChange("date_debut", val)}
                    />
                  </Grid>

                  {/* 🔴 Date Fin */}
                  <Grid item xs={12} sm={6} md={3}>
                    <DateTimePickerComponent
                      label="Date fin"
                      typeDate="date"
                      value={filters.date_fin}
                      dateSelected={(val) => handleDateFilterChange("date_fin", val)}
                    />
                  </Grid>

                </Grid>
              </Collapse>

            </CardContent>
          </Card>

          {/* Tableau des factures */}
          <Card
            elevation={3}
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',

              marginBottom: '20px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }}
          >
            <TableContainer  >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell>N° Facture</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Période</TableCell>
                    <TableCell align="right">Montant HT</TableCell>
                    <TableCell align="right">Montant TTC</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    // Affichage des lignes de chargement
                    Array.from(new Array(rowsPerPage)).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        {Array.from(new Array(8)).map((_, cellIndex) => (
                          <TableCell key={`cell-${index}-${cellIndex}`}>
                            <Box sx={{ height: 24, width: '80%', bgcolor: alpha(theme.palette.text.disabled, 0.2), borderRadius: 1 }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : factures.length > 0 ? (
                    // Affichage des factures
                    factures.map((facture) => (
                      <TableRow
                        key={facture.ID_Facture}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleViewFacture(facture.ID_Facture)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptIcon sx={{ mr: 1, color: theme.palette.text.secondary, fontSize: 20 }} />
                            <Typography variant="body2" fontWeight="medium">
                              {facture.numero_facture || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                mr: 1,
                                bgcolor: theme.palette.primary.main,
                                fontSize: '0.875rem'
                              }}
                            >
                              {facture.client?.Raison_sociale?.charAt(0) || facture.client?.Nom?.charAt(0) || '?'}
                            </Avatar>
                            <Typography variant="body2">
                              {facture.client?.Raison_sociale || facture.client?.Nom || 'Client inconnu'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(facture.date_facture)}</TableCell>
                        <TableCell>
                          {formatDate(facture.periode_debut)} - {formatDate(facture.periode_fin)}
                        </TableCell>
                        <TableCell align="right">
                          {(facture.montant_ht || 0).toLocaleString()} MAD
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {(facture.montant_ttc || 0).toLocaleString()} MAD
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={facture.statut || 'N/A'}
                            size="small"
                            color={getStatusColor(facture.statut)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="Voir">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewFacture(facture.ID_Facture);
                                }}
                                sx={{ color: theme.palette.info.main }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Modifier la facture">
                              <IconButton
                                size="Large"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFacture(facture.ID_Facture);
                                }}
                                sx={{ color: theme.palette.warning.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Télécharger PDF">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(facture);
                                  
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Exporter Excel">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadExcel(facture);
                                }}
                                sx={{ color: theme.palette.success.main }}
                              >
                                <ExcelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Supprimer">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(facture);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    // Aucune facture trouvée
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                          <ReceiptIcon sx={{ fontSize: 60, color: alpha(theme.palette.text.secondary, 0.4), mb: 2 }} />
                          <Typography variant="h6" gutterBottom>Aucune facture trouvée</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {filterOpen ? 'Essayez de modifier vos filtres ou' : 'Vous pouvez'}
                            {' créer une nouvelle facture.'}
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleCreateFacture}
                            sx={{
                              mt: 1,
                              borderRadius: 2,
                              boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}
                          >
                            Nouvelle Facture
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
            />
          </Card>
        </motion.div>

        {/* Dialogue de confirmation de suppression */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
              Confirmer la suppression
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer la facture <strong>{factureToDelete?.numero_facture}</strong> ?
              <br />
              Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
              }}
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialogue de génération de factures mensuelles */}
        <Dialog
          open={generateDialogOpen}
          onClose={() => setGenerateDialogOpen(false)}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GenerateIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
              Générer des factures mensuelles
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Sélectionnez le mois et l'année pour générer automatiquement les factures pour tous les voyages non facturés.
            </DialogContentText>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="generate-month-label">Mois</InputLabel>
                  <Select
                    labelId="generate-month-label"
                    value={generateMonth}
                    onChange={(e) => setGenerateMonth(e.target.value)}
                    label="Mois"
                  >
                    {months.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Année"
                  type="number"
                  value={generateYear}
                  onChange={(e) => setGenerateYear(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputProps={{ inputProps: { min: 2000, max: 2100 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="generate-client-label">Client (optionnel)</InputLabel>
                  <Select
                    labelId="generate-client-label"
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    label="Client (optionnel)"
                  >
                    <MenuItem value="">Tous les clients</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.ID_Client} value={client.ID_Client}>
                        {client.Raison_sociale || client.Nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setGenerateDialogOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleGenerateConfirm}
              color="primary"
              variant="contained"
              startIcon={<GenerateIcon />}
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
              }}
            >
              Générer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default FacturesList;